/**
 * Verlof-labels — pure opmaakhelpers voor het verlofformulier (VerlofModal.jsx). Geen React,
 * geen JSX. Ook de weekvelden halen hier hun voorstel, hun getalnotatie en hun teller vandaan.
 *
 * Hier en niet in de component, om dezelfde reden als lib/fteLabels.js: Node kan geen .jsx
 * importeren, en de tekst moet na te rekenen zijn zoals hij op het scherm komt. Een kopie in een
 * diagnosescript zou meten wat er niet draait.
 */

import { ALLE_DAGEN } from '../data/afwezigheid.js';
import { verdeelIngevuldeUren } from '../data/verlofuren.js';

// Tussen "wk43" en "0u" een harde spatie (U+00A0), zodat een lange periode alleen tussen de weken
// afbreekt. Via fromCharCode, zodat er geen onzichtbaar teken in de bron staat.
const HARDE_SPATIE = String.fromCharCode(0xa0);

/**
 * Een urenbedrag zoals het formulier het toont: afgerond op twee decimalen, de precisie van het
 * invoerveld. Hele uren zonder decimaal ("32"), gebroken met een komma ("13,4") — zelfde vorm als
 * urenKort in Beheer en Weekrooster. Eén plek voor de grijze regel, de weekvelden en de teller.
 *
 * ALLEEN WEERGAVE: een weekdeel met decimalen is het restant T − I en kan in de laatste bit afwijken
 * (38,40 − 25 = 13,399999999999999); zo staat er wat er bedoeld is.
 */
export function toonUren(uren) {
  return String(Math.round(uren * 100) / 100).replace('.', ',');
}

/** Het totaal van één week uit perWeek, in ALLE_DAGEN-volgorde — dezelfde volgorde als bouwAfwezigheidUrenMap. */
function weekSom(dagen) {
  let uren = 0;
  for (const dag of ALLE_DAGEN) if (dag in dagen) uren += dagen[dag];
  return uren;
}

/**
 * Het voorstel per gedekte ISO-week, in de volgorde van de periode:
 *     [{ sleutel: '2026-44', week: 'wk44', uren: 32 }, { sleutel: '2026-45', week: 'wk45', uren: 16 }]
 * null zolang er niets te verdelen valt (geen geldig getal, onbekende medewerker, geen geldige
 * periode).
 *
 * Geen eigen rekensom: het is verdeelIngevuldeUren (data/verlofuren.js), dus precies de weektotalen
 * die engine en balans lezen. De sleutels zijn de gedekte weken van het record, en dat is dezelfde
 * lijst waar urenPerWeek precies op moet passen (handmatigeWeekdelen). Weekvelden met deze
 * sleutels matchen dus per constructie.
 */
export function weekVoorstel(record, regels) {
  const verdeeld = verdeelIngevuldeUren(record, regels);
  if (!verdeeld) return null;
  return Object.entries(verdeeld.perWeek).map(([sleutel, dagen]) => (
    { sleutel, week: `wk${sleutel.split('-')[1]}`, uren: weekSom(dagen) }
  ));
}

/**
 * De grijze regel onder het urenveld: wat de app per week boekt, bijvoorbeeld
 * "wk43 0u · wk44 32u · wk45 16u". Jasper legt dat per blok naast Numbers. Het formulier toont hem
 * bij een periode van één week; bij meer weken staan er weekvelden.
 *
 * null zolang er niets te verdelen valt; dan toont het formulier niets.
 */
export function weekverdelingTekst(record, regels) {
  const voorstel = weekVoorstel(record, regels);
  if (!voorstel) return null;
  return voorstel.map(({ week, uren }) => `${week}${HARDE_SPATIE}${toonUren(uren)}u`).join(' · ');
}

/**
 * De teller onder de weekvelden: hoeveel van het totaal er verdeeld is, en of dat klopt.
 *     "48 van 48 uur verdeeld"   "46 van 48 uur verdeeld — nog 2u"   "50 van 48 uur verdeeld — 2u te veel"
 *
 * HET OORDEEL KOMT UIT DE LEESLAAG, niet uit een eigen marge. De teller geeft het record met de
 * weekbedragen aan verdeelIngevuldeUren, en die neemt ze letterlijk over of valt terug op naar rato
 * (handmatigeWeekdelen). Klopt = hij neemt ze letterlijk, dus elke week komt terug met precies het
 * ingevulde bedrag. Valt hij terug, dan komt het voorstel terug, en dat verschilt in minstens één
 * week van de ingevulde bedragen: het voorstel telt exact op tot het totaal, dus waren ze gelijk
 * geweest, dan was er niets om op terug te vallen. Zo staat het formulier nooit groen op iets dat de
 * leeslaag weigert, en staat de marge van 0,01 op één plek.
 *
 * Exact vergelijken mag. Een letterlijk overgenomen bedrag komt via dagwaardenVanWeek (8u per dag,
 * het restant op de laatste) en de optelling in ALLE_DAGEN-volgorde bit-exact terug. Valt dat ooit
 * anders uit, dan staat de teller ten onrechte rood, nooit ten onrechte groen.
 *
 * De som en het verschil zijn alleen weergave.
 *
 * @param {object} record    het record zoals het opgeslagen zou worden, met `uren` (het totaal)
 * @param {object} regels
 * @param {Object} bedragen  { [sleutel]: uren } voor elke gedekte week, in de volgorde van de periode
 * @returns {{som:number, klopt:boolean, tekst:string}}
 */
export function weekTeller(record, regels, bedragen) {
  const som = Object.values(bedragen).reduce((s, uren) => s + uren, 0);
  const verdeeld = verdeelIngevuldeUren({ ...record, urenPerWeek: bedragen }, regels);
  const klopt = !!verdeeld && Object.entries(bedragen).every(
    ([sleutel, uren]) => sleutel in verdeeld.perWeek && Object.is(weekSom(verdeeld.perWeek[sleutel]), uren),
  );
  const tekst = `${toonUren(som)} van ${toonUren(record.uren)} uur verdeeld`;
  if (klopt) return { som, klopt, tekst };
  const verschil = som - record.uren;
  return {
    som,
    klopt,
    tekst: verschil < 0 ? `${tekst} — nog ${toonUren(-verschil)}u` : `${tekst} — ${toonUren(verschil)}u te veel`,
  };
}
